<script lang="ts">
	import Layout from "../components/Layout.svelte";
	import HeadingSection from "../components/HeadingSection.svelte";
	import SolutionsSection from "../components/SolutionsSection.svelte";
	import ComputerSection from "../components/ComputerSection.svelte";
	import ContactForm from "../components/Contact/ContactForm.svelte";
	import Link from "../components/Link.svelte";
	import Icon from "../components/Icon.svelte";
	import { getTenant } from "../getTenant.js";
	import { getTranslate } from "../utils/getTranslate.js";
	import translations from "./SolutionGroupSubpage.translations.json";
	import type { SolutionGroupSubpageProps } from "./SolutionGroupSubpageProps.js";
	import { solutionGroupTexts } from "../../../core/schema/solutionGroupTexts.js";
	import { solutionGroupNameMap } from "../../../core/schema/names/solutionGroupNameMap.js";
	import { getSolutionDetailPath } from "../../../core/schema/paths/getSolutionDetailPath.js";
	import { contactPathMap } from "../../../core/schema/paths/contactPathMap.js";
	import { ourApproachPathMap } from "../../../core/schema/paths/ourApproachPathMap.js";
	import { softwarePathMap } from "../../../core/schema/paths/softwarePathMap.js";
	import StaticPicture from "../components/Picture/StaticPicture.svelte";
	import arrowRight from "../../../core/assets/icons/arrow-right.svg?raw";
	import defaultIcon from "../../../core/assets/icons/tools.svg?raw";
	import { iconMap } from "../../../core/assets/icons/iconMap.js";
	import spiralImg from "../assets/images/about/spiral.jpg";

	type $$Props = SolutionGroupSubpageProps;

	const translate = getTranslate(translations);
	const tenant = getTenant();

	const { group, groupSolutions } = $$restProps as $$Props;

	const texts = solutionGroupTexts[group];
	const groupName = solutionGroupNameMap[group][tenant.locale];
</script>

<Layout>
	<div class="container">
		<HeadingSection tag={groupName} headline={texts.heading[tenant.locale]}>
			<p class="font-lexend lg:text-md/normal mb-5 font-light tracking-[0.02em]">{texts.description1[tenant.locale]}</p>
			<Link
				asButton
				variant="secondary"
				class="mt-12 xl:mt-16"
				icon={arrowRight}
				href="/{contactPathMap[tenant.locale]}"
			>
				{texts.ctaLabel[tenant.locale]}
			</Link>
		</HeadingSection>
	</div>

	{#if groupSolutions.length > 0}
		<div class="border-lttertiary mt-8 border-y md:mt-0">
			<div class="mx-auto max-w-[120rem]">
				<h2 class="text-ltprimary py-8 text-center font-light uppercase tracking-wider">
					{groupName}
				</h2>
			</div>
		</div>
		<div class="border-lttertiary border-b">
			<div class="mx-auto max-w-[120rem] px-4 md:px-16 xl:px-32">
				<div class="border-lttertiary grid grid-cols-2 border-l md:grid-cols-3 lg:grid-cols-4">
					{#each groupSolutions as solution (solution.slug)}
						<a
							href="/{getSolutionDetailPath(solution)[tenant.locale]}"
							class="bg-lttertiary-200 border-lttertiary group relative flex min-h-40 flex-col gap-6 overflow-hidden border-b border-r p-6"
						>
							<div
								class="bg-lttertiary-300 absolute inset-0 translate-y-full transition-transform duration-300 ease-in-out group-hover:translate-y-0"
							></div>
							<Icon icon={iconMap[solution.icon ?? ""] ?? defaultIcon} class="text-ltprimary relative size-10" />
							<p class="text-ltprimary relative text-xs font-normal uppercase tracking-[0.02em]">
								{solution.name[tenant.locale]}
							</p>
						</a>
					{/each}
				</div>
			</div>
		</div>
	{/if}

	<div class="container">
		<section class="mt-16 lg:mt-24">
			<div
				class="mx-4 flex flex-col md:mx-16 md:grid md:grid-cols-2 md:items-center md:gap-8 lg:gap-16 xl:mx-32 xl:gap-32"
			>
				<div
					class="relative order-2 -mx-4 mt-8 aspect-square w-[calc(100%+2rem)] overflow-hidden md:order-1 md:mx-0 md:mt-0 md:w-full"
				>
					<StaticPicture
						image={spiralImg}
						alt=""
						width={768}
						height={768}
						class="absolute inset-0"
						imgClass="absolute inset-0 size-full object-contain"
					/>
				</div>

				<div class="order-1 flex flex-col justify-center md:order-2">
					<div class="flex items-center gap-4">
						<span class="bg-ltprimary-200 h-px w-8"></span>
						<span class="text-ltprimary-200 uppercase">{translate("approachTag")}</span>
					</div>
					<h2 class="text-ltprimary mt-8 p-0 font-light uppercase tracking-wider">
						{translate("approachHeadline")}
					</h2>
					<p class="text-ltsecondary lg:text-md/normal mt-6 tracking-[0.02em]">
						{translate("approachDesc1")}
					</p>
					<p class="text-ltsecondary lg:text-md/normal mt-4 tracking-[0.02em]">
						{translate("approachDesc2")}
					</p>
					<Link
						asButton
						variant="secondary"
						class="mt-10 self-start xl:mt-14"
						icon={arrowRight}
						href="/{ourApproachPathMap[tenant.locale]}"
					>
						{translate("approachCta")}
					</Link>
				</div>
			</div>
		</section>
	</div>

	<section class="!mb-0">
		<div class="mx-4 mb-8 text-center md:mx-16 xl:mx-32">
			<h2 class="text-ltprimary font-light uppercase tracking-wider">
				{translate("solutionsHeadline")}
			</h2>
			<p class="text-ltsecondary lg:text-md/normal mx-auto mt-4 max-w-3xl tracking-[0.02em]">
				{translate("solutionsDesc")}
			</p>
		</div>
		<SolutionsSection />
	</section>

	<div class="container">
		<section class="!mb-16 mt-16 lg:!mb-24 lg:mt-24">
			<ComputerSection>
				<div class="mb-4 flex items-center gap-4 lg:mb-8">
					<span class="bg-ltprimary-200 h-px w-8"></span>
					<span class="text-ltprimary-200 uppercase">{translate("softwareTag")}</span>
				</div>
				<h2 class="text-ltprimary mt-4 items-start p-0 text-center font-light uppercase tracking-[0.02em] md:text-left">
					{translate("softwareHeadline")}
				</h2>
				<p class="text-ltsecondary lg:text-md/normal mt-6 tracking-[0.02em]">
					{translate("softwareDesc")}
				</p>
				<div class="mt-6 flex flex-wrap gap-4 lg:mt-10">
					<Link asButton variant="secondary" icon={arrowRight} href="/{softwarePathMap[tenant.locale]}">
						{translate("softwareCta")}
					</Link>
					<Link asButton variant="secondary" icon={arrowRight} href="/{softwarePathMap[tenant.locale]}">
						{translate("softwarePackagesCta")}
					</Link>
				</div>
			</ComputerSection>
		</section>
	</div>

	<section class="!mb-0">
		<ContactForm />
	</section>
</Layout>
